import { useState, useEffect, Fragment } from 'react';

import * as Styled from './ShowPhoneNumberButton.styled';

import { useUserContext } from '../../../contexts/UserContext';
import { useVerificationContext } from '../../../contexts/VerificationContext';
import { useOnUpdateOnly } from '../../../hooks/UseOnUpdateOnly';

import { LOADING_COLOUR, Loading } from '../Loading/Loading';
import type { ShowPhoneNumberButtonProps } from './ShowPhoneNumberButton.typed';

import { CDN_STATIC_ASSETS } from '../../../utils';
import { BUTTON_TYPE } from '../Button/SharedButton';

const ShowPhoneNumberButton = (props: ShowPhoneNumberButtonProps) => {
  const {
    onReveal,
    showNumberTextVariant,
    onCall,
    isPrimaryButton,
    adId,
    ...rest
  } = props;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { cookieBasedUser } = useUserContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();

  useOnUpdateOnly(() => {
    setPhoneNumber('');
  }, [adId]);

  const handleReveal = async () => {
    setIsLoading(true);
    const phoneNumberResponse = await onReveal();
    setIsLoading(false);
    if (phoneNumberResponse) {
      setPhoneNumber(phoneNumberResponse);
    }
  };

  useEffect(() => {
    if (triggerAfterVerify && cookieBasedUser) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerAfterVerify]);

  const ofType = isPrimaryButton ? BUTTON_TYPE.PRIMARY : BUTTON_TYPE.SECONDARY;

  const showPhoneNumberButtonCopy = `Show${
    showNumberTextVariant ? '' : ' Phone'
  } Number`;

  const renderShowPhoneNumberButtonContent = isLoading ? (
    <Loading
      cdnUrl={CDN_STATIC_ASSETS}
      color={isPrimaryButton ? LOADING_COLOUR.LIGHT : LOADING_COLOUR.DARK}
    />
  ) : (
    showPhoneNumberButtonCopy
  );

  return (
    <Fragment>
      {phoneNumber && (
        <Styled.CallPhoneNumberButtonLink
          ofType={ofType}
          data-testid="call-phone-number"
          href={`tel:${phoneNumber}`}
          onClick={onCall}
          {...rest}
        >
          <Styled.ShowPhoneNumberText>{`Call ${phoneNumber}`}</Styled.ShowPhoneNumberText>
        </Styled.CallPhoneNumberButtonLink>
      )}
      {!phoneNumber && (
        <Styled.ShowPhoneNumberButton
          ofType={ofType}
          data-testid="view-phone-number"
          onClick={handleReveal}
          {...rest}
        >
          {renderShowPhoneNumberButtonContent}
        </Styled.ShowPhoneNumberButton>
      )}
    </Fragment>
  );
};

export { ShowPhoneNumberButton };
